import React from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCalendarDays, formatDateKey, isToday } from '../utils/dateUtils';
import { DayData } from '../types';
import { getWeekNumber, getPhaseForWeek, CATEGORY_COLORS } from '../data/studyPlan';
import './Calendar.css';

interface CalendarProps {
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  onDateClick: (date: Date) => void;
  dayData: Record<string, DayData>;
  selectedDate: Date | null;
}

export const Calendar: React.FC<CalendarProps> = ({
  currentMonth,
  onMonthChange,
  onDateClick,
  dayData,
  selectedDate
}) => {
  const calendarDays = getCalendarDays(currentMonth);
  const monthYear = format(currentMonth, 'MMMM yyyy');

  const handlePreviousMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    if (newMonth.getMonth() >= 6) { // July or later
      onMonthChange(newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    if (newMonth.getFullYear() === 2025 || (newMonth.getFullYear() === 2026 && newMonth.getMonth() === 0)) {
      onMonthChange(newMonth);
    }
  };

  const getDayColor = (date: Date): string => {
    const dateKey = formatDateKey(date);
    const day = dayData[dateKey];
    
    if (!day || day.tasks.length === 0) return '#ffffff';
    
    // Find the primary task category for the day
    const categoryCount = day.tasks.reduce((acc, task) => {
      acc[task.category] = (acc[task.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const primaryCategory = Object.entries(categoryCount).reduce((a, b) => 
      categoryCount[a[0]] > categoryCount[b[0]] ? a : b
    )[0];
    
    return CATEGORY_COLORS[primaryCategory as keyof typeof CATEGORY_COLORS] || '#ffffff';
  };

  const isCompleted = (date: Date): boolean => {
    const dateKey = formatDateKey(date);
    return dayData[dateKey]?.completed || false;
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button onClick={handlePreviousMonth} className="nav-button">
          <ChevronLeft size={20} />
        </button>
        <h2>{monthYear}</h2>
        <button onClick={handleNextMonth} className="nav-button">
          <ChevronRight size={20} />
        </button>
      </div>
      
      <div className="calendar-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>
      
      <div className="calendar-grid">
        {calendarDays.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="calendar-day empty" />;
          }
          
          const weekNumber = getWeekNumber(date);
          const phase = getPhaseForWeek(weekNumber);
          const completed = isCompleted(date);
          const selected = selectedDate && formatDateKey(date) === formatDateKey(selectedDate);
          
          return (
            <div
              key={formatDateKey(date)}
              className={`calendar-day ${isToday(date) ? 'today' : ''} ${completed ? 'completed' : ''} ${selected ? 'selected' : ''}`}
              style={{ backgroundColor: getDayColor(date) }}
              onClick={() => onDateClick(date)}
            >
              <div className="day-number">{format(date, 'd')}</div>
              <div className="phase-indicator" title={phase.name}>
                {phase.name[0]}
              </div>
              {completed && <div className="completion-mark">✓</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};